import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  LinearProgress,
  Alert,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { 
  CloudUpload as UploadIcon,
  OpenInNew as OpenIcon,
  Delete as DeleteIcon,
  Assignment as TaskIcon,
  Description as FileIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  MenuBook as LibraryBooksIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
  CloudDownload as DownloadIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const StudentWorkloadSubmission = () => {
  const navigate = useNavigate();
  const [taskData, setTaskData] = useState<any>(null);
  const [researchTitleTh, setResearchTitleTh] = useState('');
  const [researchSubjectName, setResearchSubjectName] = useState('');
  const [researchAbstract, setResearchAbstract] = useState('');
  const [researchKeywords, setResearchKeywords] = useState('');
  const [selectedArticleFile, setSelectedArticleFile] = useState<File | null>(null);
  const [selectedCertFile, setSelectedCertFile] = useState<File | null>(null);
  const [articleExternalUrl, setArticleExternalUrl] = useState('');
  const [certExternalUrl, setCertExternalUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [selectedPlacementId, setSelectedPlacementId] = useState<string>('');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');
  const [examResultDialogOpen, setExamResultDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const resolveFileUrl = (url?: string) => {
    if (!url) return '';
    if (window.location.protocol === 'https:' && url.startsWith('http://')) {
      url = url.replace(/^http:\/\//i, 'https://');
    }
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/uploads/')) return `/api/v1${url}`;
    if (!url.startsWith('/')) return `/api/v1/uploads/${url}`;
    return url;
  };

  const isImageFile = (url?: string) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
  };

  const handleOpenFile = (url: string, name?: string) => {
    const fullUrl = resolveFileUrl(url);
    if (isImageFile(fullUrl)) {
      setPreviewFileUrl(fullUrl);
      setPreviewFileName(name || 'ภาพหลักฐานภาระงาน');
      setPreviewModalOpen(true);
    } else {
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const fetchMyTasks = async (pId?: string) => {
    try {
      setLoading(true);
      setErrorMessage('');
      const url = pId ? `/workload-evaluations/my-tasks?placement_id=${pId}` : '/workload-evaluations/my-tasks';
      const res = await api.get(url);
      setTaskData(res.data);
      if (res.data?.placement_id) {
        setSelectedPlacementId(res.data.placement_id);
      }
    } catch (err: any) {
      console.error('Failed to load my workload tasks:', err);
      setErrorMessage(err.response?.data?.message || err.response?.data?.error || 'เกิดข้อผิดพลาดในการโหลดข้อมูลภาระงาน');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTasks();
  }, []);

  const handleOpenUpload = (item: any) => {
    setSelectedItem(item);
    setSelectedFile(null);
    setNotes('');
    setExternalUrl('');
    const isResearch = Boolean(
      item && (
        item.item_type === 'RESEARCH' ||
        (item.title && item.title.includes('วิจัย')) ||
        (item.description && item.description.includes('วิจัย'))
      )
    );
    if (isResearch && taskData?.research_info) {
      setResearchTitleTh(taskData.research_info.title_th || '');
      setResearchSubjectName(taskData.research_info.subject_name || '');
      setResearchAbstract(taskData.research_info.abstract || '');
      setResearchKeywords(taskData.research_info.keywords || '');
    } else if (isResearch) {
      setResearchTitleTh('');
      setResearchSubjectName('');
      setResearchAbstract('');
      setResearchKeywords('');
    }
    setUploadDialogOpen(true);
  };

  const handleCloseUpload = () => {
    setUploadDialogOpen(false);
    setSelectedItem(null);
    setSelectedFile(null);
    setSelectedArticleFile(null);
    setSelectedCertFile(null);
    setArticleExternalUrl('');
    setCertExternalUrl('');
  };

  const ALLOWED_DOC_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_DOC_EXTENSIONS.includes(ext)) {
        alert(`ไม่อนุญาตให้อัปโหลดไฟล์ ${ext}\nระบบจำกัดเฉพาะไฟล์เอกสาร (${ALLOWED_DOC_EXTENSIONS.join(', ')}) เท่านั้น เพื่อความปลอดภัยของระบบ`);
        e.target.value = '';
        setSelectedFile(null);
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        alert('ขนาดไฟล์ต้องไม่เกิน 50 MB');
        e.target.value = '';
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmitArtifact = async () => {
    if (!taskData || !selectedItem) return;
    if (!selectedFile && !externalUrl.trim()) {
      alert('กรุณาเลือกไฟล์แนบ หรือใส่ลิงก์หลักฐานผลงาน');
      return;
    }

    const isResearch = Boolean(
      selectedItem && (
        selectedItem.item_type === 'RESEARCH' ||
        (selectedItem.title && selectedItem.title.includes('วิจัย')) ||
        (selectedItem.description && selectedItem.description.includes('วิจัย'))
      )
    );

    if (isResearch) {
      if (!researchTitleTh.trim()) {
        alert('กรุณาระบุชื่อเรื่องงานวิจัยในชั้นเรียน (ภาษาไทย) ก่อนส่งงาน');
        return;
      }
      if (!researchSubjectName.trim()) {
        alert('กรุณาระบุรายวิชาที่ทำวิจัย ก่อนส่งงาน');
        return;
      }
      if (!researchAbstract.trim()) {
        alert('กรุณาระบุบทคัดย่อ (Abstract) ก่อนส่งงาน');
        return;
      }
      if (!researchKeywords.trim()) {
        alert('กรุณาระบุคำสำคัญ (Keywords) ก่อนส่งงาน');
        return;
      }
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('placement_id', taskData.placement_id);
      formData.append('item_id', selectedItem.id);
      if (selectedFile) formData.append('file', selectedFile);
      if (externalUrl) formData.append('file_url_external', externalUrl);
      if (notes) formData.append('notes', notes);
      if (isResearch) {
        formData.append('research_title_th', researchTitleTh.trim());
        formData.append('research_subject_name', researchSubjectName.trim());
        formData.append('research_abstract', researchAbstract.trim());
        formData.append('research_keywords', researchKeywords.trim());
        if (selectedArticleFile) formData.append('article_file', selectedArticleFile);
        if (articleExternalUrl.trim()) formData.append('article_url_external', articleExternalUrl.trim());
        if (selectedCertFile) formData.append('utilization_cert_file', selectedCertFile);
        if (certExternalUrl.trim()) formData.append('utilization_cert_url_external', certExternalUrl.trim());
      }

      await api.post('/workload-evaluations/submissions', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert('อัปโหลดไฟล์ภาระงานเรียบร้อยแล้ว');
      handleCloseUpload();
      fetchMyTasks();
    } catch (err: any) {
      console.error(err);
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการอัปโหลดไฟล์');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArtifact = async (submissionId: string) => {
    if (window.confirm('ยืนยันการลบไฟล์งานนี้?')) {
      try {
        setLoading(true);
        await api.delete(`/workload-evaluations/submissions/${submissionId}`);
        fetchMyTasks();
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการลบไฟล์');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading && !taskData) {
    return <LinearProgress sx={{ mt: 3 }} />;
  }

  if (!taskData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={errorMessage ? "warning" : "info"} sx={{ borderRadius: 2 }}>
          {errorMessage || 'ไม่พบข้อมูลการออกฝึกประสบการณ์วิชาชีพที่เปิดใช้งาน หรือท่านยังไม่ได้ลงทะเบียนในระบบ'}
        </Alert>
      </Box>
    );
  }

  const items = taskData.items || [];
  const submissions = taskData.submissions || [];
  const scores = taskData.scores_json || {};
  const isEvaluated = Boolean(taskData.is_evaluated || taskData.evaluation_status === 'ประเมินแล้ว' || (taskData.total_score !== null && taskData.total_score !== undefined));

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
          <TaskIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
          ส่งภาระงานตามรายวิชา (Student Workload Submission)
        </Typography>
        <Typography variant="body2" color="textSecondary">
          ส่งไฟล์หลักฐานภาระงานตามเกณฑ์ของรายวิชาที่ลงทะเบียน เพื่อให้อาจารย์นิเทศก์ตรวจประเมิน
        </Typography>
      </Box>

      {/* Placement & Course Banner */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}>
        {taskData.available_placements && taskData.available_placements.length > 1 && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'white', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
              เลือกลงทะเบียนวิชาที่ต้องการส่งภาระงาน:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 320 }}>
              <Select
                value={selectedPlacementId || taskData.placement_id}
                onChange={(e) => {
                  setSelectedPlacementId(e.target.value);
                  fetchMyTasks(e.target.value);
                }}
              >
                {taskData.available_placements.map((p: any) => (
                  <MenuItem key={p.placement_id} value={p.placement_id}>
                    {p.course_code} {p.course_name} (ภาคเรียน {p.semester}/{p.academic_year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip label={`รหัสวิชา ${taskData.course_code}`} color="primary" sx={{ fontWeight: 700 }} />
              {taskData.curriculum_year && (
                <Chip label={`หลักสูตร พ.ศ. ${taskData.curriculum_year}`} variant="outlined" />
              )}
              <Chip label={`ภาคเรียนที่ ${taskData.semester}/${taskData.academic_year}`} variant="outlined" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {taskData.course_name}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              สถานศึกษาฝึกสอน: <strong>{taskData.school_name}</strong> | แบบประเมิน: <strong>{taskData.workload_form_title || 'ยังไม่มีแบบประเมิน'}</strong>
            </Typography>
            {!taskData.workload_form_id && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                รายวิชานี้ยังไม่ได้กำหนดแบบประเมินภาระงาน โปรดติดต่อผู้รับผิดชอบหลักสูตร/อาจารย์นิเทศก์
              </Alert>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, display: 'inline-block', minWidth: 180, textAlign: 'center', border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>
                สถานะการประเมินภาระงาน
              </Typography>
              <Box sx={{ my: 1 }}>
                {isEvaluated ? (
                  <Chip label="ประเมินแล้ว" size="small" color="success" sx={{ fontWeight: 700 }} />
                ) : (
                  <Chip label="รอประเมิน" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                )}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                {isEvaluated ? 'อาจารย์นิเทศก์ประเมินแล้ว' : 'รออาจารย์นิเทศก์ประเมิน'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {isEvaluated && (
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            💡 อาจารย์นิเทศก์ได้บันทึกผลการประเมินภาระงานแล้ว — ท่านยังสามารถส่งงานเพิ่มเติมหรือแก้ไขไฟล์ภาระงานได้
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            หากท่านส่งงานเพิ่มเติมหรือแก้ไขไฟล์หลังจากอาจารย์ประเมินแล้ว ระบบจะแจ้งเตือนไปยังอาจารย์ผู้ประเมินโดยอัตโนมัติ และท่านสามารถติดต่ออาจารย์นิเทศก์ประจำสาขาวิชาเพื่อขอให้พิจารณาตรวจและแก้ไขคะแนนภาระงานได้
          </Typography>
        </Alert>
      )}

      {/* Workload Requirements Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: 60 }}>รหัส</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 140 }}>หมวด/ส่วน</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>รายการภาระงาน</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>คะแนนเต็ม</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>สถานะการส่งงาน / ไฟล์แนบ</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: 110 }}>สถานะการประเมิน</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: 120 }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item: any) => {
              const sub = submissions.find((s: any) => s.item_id === item.id);
              const itemScore = scores[item.id];
              const hasScore = itemScore !== undefined && itemScore !== '';

              return (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{item.item_code || '-'}</TableCell>
                  <TableCell>
                    <Chip label={item.section_name || 'ภาระงาน'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                    {item.description && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                        {item.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {Number(item.max_score) > 0 ? (
                      <Chip 
                        label={`${item.max_score} คะแนน`} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      <Chip 
                        label="ไม่คิดคะแนน" 
                        size="small" 
                        color="info" 
                        variant="outlined" 
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {sub ? (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <FileIcon color="primary" fontSize="small" />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {sub.file_name || 'ไฟล์แนบภาระงาน'}
                          </Typography>
                          {sub.review_status === 'RESUBMITTED' && (
                            <Chip 
                              label={`แก้ไขแล้ว รอตรวจซ้ำ (รอบที่ ${sub.revision_count || 2})`} 
                              color="info" 
                              size="small" 
                              sx={{ fontWeight: 700 }} 
                            />
                          )}
                          {sub.review_status === 'NEEDS_REVISION' && (
                            <Chip 
                              label={`อาจารย์ส่งกลับแก้ไข (รอบที่ ${sub.revision_count || 1})`} 
                              color="warning" 
                              size="small" 
                              sx={{ fontWeight: 700 }} 
                            />
                          )}
                          {sub.review_status === 'APPROVED' && (
                            <Chip 
                              label="ผ่าน (Approved)" 
                              color="success" 
                              size="small" 
                              sx={{ fontWeight: 700 }} 
                            />
                          )}
                          {(sub.review_status === 'SUBMITTED' || !sub.review_status) && (
                            <Chip 
                              label={`รออาจารย์ตรวจ (รอบที่ ${sub.revision_count || 1})`} 
                              color="info" 
                              size="small" 
                              variant="outlined" 
                              sx={{ fontWeight: 600 }} 
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          ส่งเมื่อ: {new Date(sub.submitted_at).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          {sub.notes && ` (หมายเหตุ: ${sub.notes})`}
                        </Typography>

                        {/* Supervisor Feedback Box */}
                        {(sub.feedback_comment || sub.feedback_file_url) && (
                          <Paper variant="outlined" sx={{ mt: 1, p: 1.2, bgcolor: sub.review_status === 'NEEDS_REVISION' ? '#fffbeb' : '#f0fdf4', borderColor: sub.review_status === 'NEEDS_REVISION' ? '#fcd34d' : '#86efac', borderRadius: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: sub.review_status === 'NEEDS_REVISION' ? '#b45309' : '#15803d', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {sub.review_status === 'NEEDS_REVISION' && <WarningIcon sx={{ fontSize: 14 }} />}
                              💬 คำแนะนำ/ข้อเสนอแนะจากอาจารย์นิเทศก์:
                            </Typography>
                            {sub.feedback_comment && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', mt: 0.3, fontWeight: 500 }}>
                                {sub.feedback_comment}
                              </Typography>
                            )}
                            {sub.feedback_file_url && (
                              <Box sx={{ mt: 0.8 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  href={resolveFileUrl(sub.feedback_file_url)}
                                  target="_blank"
                                  startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                  sx={{ fontSize: '0.72rem', py: 0.2, textTransform: 'none', fontWeight: 700 }}
                                >
                                  {sub.feedback_file_name ? `ดาวน์โหลดไฟล์คำแนะนำ (${sub.feedback_file_name})` : 'ดาวน์โหลดไฟล์คำแนะนำจากอาจารย์'}
                                </Button>
                              </Box>
                            )}
                          </Paper>
                        )}
                      </Box>
                    ) : (
                      <Chip label="ยังไม่ได้ส่งงาน" size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {Number(item.max_score) === 0 ? (
                      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                        ส่งเก็บหลักฐาน
                      </Typography>
                    ) : hasScore ? (
                      <Chip label="ประเมินแล้ว" size="small" color="success" sx={{ fontWeight: 700 }} />
                    ) : (
                      <Chip label="รอประเมิน" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {sub?.file_url && (
                        <Tooltip title={isImageFile(sub.file_url) ? "ดูรูปภาพผลงาน" : "เปิดดูเอกสาร/ลิงก์"}>
                          <IconButton 
                            size="small" 
                            color="info" 
                            onClick={() => handleOpenFile(sub.file_url, sub.file_name)}
                          >
                            {isImageFile(sub.file_url) ? <ViewIcon fontSize="small" /> : <OpenIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title={sub?.review_status === 'NEEDS_REVISION' ? "ส่งไฟล์งานฉบับแก้ไขตามคำแนะนำของอาจารย์" : (sub ? "อัปโหลดไฟล์ใหม่แทนที่" : "ส่งไฟล์งาน")}>
                        <Button
                          variant={sub?.review_status === 'NEEDS_REVISION' ? "contained" : (sub ? "outlined" : "contained")}
                          color={sub?.review_status === 'NEEDS_REVISION' ? "warning" : (isEvaluated && !sub ? "warning" : "primary")}
                          size="small"
                          startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleOpenUpload(item)}
                          sx={{ fontSize: '0.75rem', py: 0.5, fontWeight: sub?.review_status === 'NEEDS_REVISION' ? 700 : 'normal' }}
                        >
                          {sub?.review_status === 'NEEDS_REVISION' ? 'ส่งไฟล์ฉบับแก้ไข' : (sub ? (sub.review_status === 'RESUBMITTED' ? 'ส่งแก้ไขเพิ่มเติม' : 'แก้ไขไฟล์') : (isEvaluated ? 'ส่งงานย้อนหลัง' : 'ส่งงาน'))}
                        </Button>
                      </Tooltip>

                      {sub && (
                        <Tooltip title="ลบไฟล์งาน">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteArtifact(sub.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Comprehensive Exam Requirement Row (if requires_exam === 1) */}
            {taskData.requires_exam === 1 && (
              <TableRow sx={{ bgcolor: '#f0f9ff' }} hover>
                <TableCell sx={{ fontWeight: 600 }}>EXAM</TableCell>
                <TableCell>
                  <Chip label="การสอบประมวลผลความรู้" size="small" color="info" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                    <QuizIcon sx={{ mr: 1, fontSize: 18, color: 'info.main' }} />
                    การสอบประมวลผลความรู้ (Comprehensive Exam)
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                    การสอบประมวลความรู้ตามเกณฑ์รายวิชา คะแนนที่ได้จะนำมาคำนวณรวมกับคะแนนภาระงานโดยอัตโนมัติ
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={`${taskData.exam_set_max_points || 0} คะแนน`} 
                    size="small" 
                    color="info" 
                    variant="outlined" 
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  {taskData.exam_submission_status === 'สอบแล้ว' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                          สอบเรียบร้อยแล้ว
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          คะแนนดิบที่ทำได้: {taskData.exam_attempt_points || 0} คะแนน
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Chip label="ยังไม่ได้ทำแบบทดสอบ" size="small" color="warning" />
                  )}
                </TableCell>
                <TableCell align="center">
                  {taskData.exam_submission_status === 'สอบแล้ว' ? (
                    <Chip 
                      label={`ได้ ${taskData.exam_score !== null && taskData.exam_score !== undefined ? Number(taskData.exam_score).toFixed(2) : '0.00'} คะแนน`} 
                      size="small" 
                      color="success" 
                      sx={{ fontWeight: 700 }} 
                    />
                  ) : (
                    <Chip label="รอสอบ" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Button
                    variant={taskData.exam_submission_status === 'สอบแล้ว' ? "outlined" : "contained"}
                    color="info"
                    size="small"
                    startIcon={<QuizIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      if (taskData.exam_submission_status === 'สอบแล้ว') {
                        setExamResultDialogOpen(true);
                      } else {
                        navigate('/dashboard/exams');
                      }
                    }}
                    sx={{ fontSize: '0.75rem', py: 0.5, fontWeight: 700 }}
                  >
                    {taskData.exam_submission_status === 'สอบแล้ว' ? 'ดูผลสอบ' : 'เข้าสอบ'}
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  รายวิชานี้ยังไม่มีการกำหนดรายการภาระงาน
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUpload} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          ส่งภาระงาน: {selectedItem?.title} ({selectedItem?.max_score} คะแนน)
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
              หมวด: <strong>{selectedItem?.section_name}</strong> | รหัสข้อ: <strong>{selectedItem?.item_code || '-'}</strong>
            </Typography>
            {selectedItem?.description && (
              <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: '0.85rem' }}>
                {selectedItem.description}
              </Alert>
            )}
          </Box>

          {/* Feedback Reminder Banner inside Dialog */}
          {(() => {
            const currentSub = taskData?.submissions?.find((s: any) => s.item_id === selectedItem?.id);
            if (currentSub?.review_status === 'NEEDS_REVISION' || currentSub?.feedback_comment || currentSub?.feedback_file_url) {
              return (
                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    ⚠️ งานชิ้นนี้อาจารย์ส่งกลับเพื่อขอให้ปรับปรุงแก้ไข:
                  </Typography>
                  {currentSub.feedback_comment && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <strong>คำแนะนำจากอาจารย์:</strong> {currentSub.feedback_comment}
                    </Typography>
                  )}
                  {currentSub.feedback_file_url && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        href={resolveFileUrl(currentSub.feedback_file_url)}
                        target="_blank"
                        startIcon={<DownloadIcon />}
                        sx={{ fontWeight: 700 }}
                      >
                        ดาวน์โหลดไฟล์คำแนะนำ/ตรวจแก้ ({currentSub.feedback_file_name || 'ไฟล์คำแนะนำ'})
                      </Button>
                    </Box>
                  )}
                </Alert>
              );
            }
            return null;
          })()}

          {/* Research Details Required Section */}
          {Boolean(
            selectedItem && (
              selectedItem.item_type === 'RESEARCH' ||
              (selectedItem.title && selectedItem.title.includes('วิจัย')) ||
              (selectedItem.description && selectedItem.description.includes('วิจัย'))
            )
          ) && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: '#f0f9ff', borderRadius: 2, border: '1px solid #bae6fd' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0369a1', display: 'flex', alignItems: 'center', mb: 1 }}>
                <LibraryBooksIcon sx={{ mr: 1, fontSize: 20 }} /> ข้อมูลงานวิจัยในชั้นเรียน (เชื่อมโยงฐานข้อมูลงานวิจัยเพื่อการสืบค้น)
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                กรุณาระบุข้อมูลงานวิจัยในชั้นเรียนให้ครบถ้วนทุกช่อง เพื่อบันทึกลงสู่ระบบฐานข้อมูลงานวิจัยในชั้นเรียน
              </Typography>
              <TextField
                fullWidth
                required
                size="small"
                label="ชื่อเรื่องงานวิจัยในชั้นเรียน (ภาษาไทย) *"
                placeholder="เช่น การพัฒนาผลสัมฤทธิ์ทางการเรียนวิชาวิทยาศาสตร์..."
                value={researchTitleTh}
                onChange={(e) => setResearchTitleTh(e.target.value)}
                sx={{ mb: 1.5, bgcolor: '#ffffff' }}
              />
              <TextField
                fullWidth
                required
                size="small"
                label="รายวิชาที่ทำวิจัย *"
                placeholder="เช่น วิทยาศาสตร์กายภาพ, ภาษาไทยพื้นฐาน..."
                value={researchSubjectName}
                onChange={(e) => setResearchSubjectName(e.target.value)}
                sx={{ mb: 1.5, bgcolor: '#ffffff' }}
              />
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                size="small"
                label="บทคัดย่อ (Abstract) *"
                placeholder="ระบุบทคัดย่อของงานวิจัย วัตถุประสงค์ กลุ่มเป้าหมาย และผลการวิจัยโดยสรุป..."
                value={researchAbstract}
                onChange={(e) => setResearchAbstract(e.target.value)}
                sx={{ mb: 1.5, bgcolor: '#ffffff' }}
              />
              <TextField
                fullWidth
                required
                size="small"
                label="คำสำคัญ (Keywords) *"
                placeholder="เช่น การจัดการเรียนรู้แบบสืบเสาะ, ผลสัมฤทธิ์ทางการเรียน (คั่นด้วยจุลภาค)"
                value={researchKeywords}
                onChange={(e) => setResearchKeywords(e.target.value)}
                sx={{ bgcolor: '#ffffff' }}
              />
            </Box>
          )}

          <Box sx={{ border: '2px dashed #90caf9', borderRadius: 2, p: 3, textAlign: 'center', mb: 2, bgcolor: '#fbfdff' }}>
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {Boolean(
                selectedItem && (
                  selectedItem.item_type === 'RESEARCH' ||
                  (selectedItem.title && selectedItem.title.includes('วิจัย')) ||
                  (selectedItem.description && selectedItem.description.includes('วิจัย'))
                )
              ) ? 'เลือกไฟล์รายงานวิจัยในชั้นเรียน 5 บท (ไฟล์หลัก)' : 'เลือกไฟล์เอกสารหลักฐานภาระงาน'}
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
              (รองรับเฉพาะเอกสาร PDF, Word, Excel, PowerPoint สูงสุด 50MB)
            </Typography>
            <input 
              type="file" 
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              onChange={handleFileChange}
              style={{ display: 'block', margin: '0 auto' }}
            />
            {selectedFile && (
              <Typography variant="caption" sx={{ color: 'success.main', display: 'block', mt: 1, fontWeight: 700 }}>
                เลือกแล้ว: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
          </Box>

          {/* Research Additional Files: Article & Certificate */}
          {Boolean(
            selectedItem && (
              selectedItem.item_type === 'RESEARCH' ||
              (selectedItem.title && selectedItem.title.includes('วิจัย')) ||
              (selectedItem.description && selectedItem.description.includes('วิจัย'))
            )
          ) && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#166534', mb: 1.5 }}>
                📎 แนบไฟล์บทความวิจัย และ หนังสือรับรองการนำไปใช้ประโยชน์ (สำหรับเผยแพร่ในคลังงานวิจัย)
              </Typography>
              
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#166534', display: 'block', mb: 0.5 }}>
                  1. ไฟล์บทความวิจัย (Research Article) (PDF/Word สูงสุด 50MB):
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedArticleFile(e.target.files[0]);
                    }
                  }}
                  style={{ display: 'block', fontSize: '0.82rem' }}
                />
                {selectedArticleFile && (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, display: 'block', mt: 0.5 }}>
                    เลือกแล้ว: {selectedArticleFile.name}
                  </Typography>
                )}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="หรือระบุลิงก์ภายนอก เช่น Google Drive ของบทความวิจัย"
                  value={articleExternalUrl}
                  onChange={(e) => setArticleExternalUrl(e.target.value)}
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#b45309', display: 'block', mb: 0.5 }}>
                  2. หนังสือรับรองการนำไปใช้ประโยชน์ (PDF/Word/ภาพ สูงสุด 50MB):
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedCertFile(e.target.files[0]);
                    }
                  }}
                  style={{ display: 'block', fontSize: '0.82rem' }}
                />
                {selectedCertFile && (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, display: 'block', mt: 0.5 }}>
                    เลือกแล้ว: {selectedCertFile.name}
                  </Typography>
                )}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="หรือระบุลิงก์ภายนอก เช่น Google Drive ของหนังสือรับรองฯ"
                  value={certExternalUrl}
                  onChange={(e) => setCertExternalUrl(e.target.value)}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            label="หรือใส่ลิงก์หลักฐานภาระงาน (เช่น Google Drive, YouTube, เว็บไซต์)"
            placeholder="https://drive.google.com/..."
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label="หมายเหตุหรือคำอธิบายเพิ่มเติมสำหรับอาจารย์นิเทศก์"
            placeholder="พิมพ์ข้อความถึงอาจารย์นิเทศก์..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseUpload}>ยกเลิก</Button>
          <Button onClick={handleSubmitArtifact} variant="contained" color="primary" disabled={loading}>
            {loading ? 'กำลังอัปโหลด...' : 'ยืนยันการส่งงาน'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox / Preview Dialog */}
      <Dialog 
        open={previewModalOpen} 
        onClose={() => setPreviewModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {previewFileName}
          </Typography>
          <IconButton size="small" onClick={() => setPreviewModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ textAlign: 'center', bgcolor: '#121212', p: 1 }}>
          <img 
            src={previewFileUrl} 
            alt={previewFileName}
            style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 4 }} 
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            href={previewFileUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            startIcon={<OpenIcon />}
          >
            เปิดในแท็บใหม่ / ดาวน์โหลด
          </Button>
          <Button variant="contained" onClick={() => setPreviewModalOpen(false)}>
            ปิด
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exam Result Dialog (Student Workload View) */}
      <Dialog 
        open={examResultDialogOpen} 
        onClose={() => setExamResultDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1E3A8A', color: 'white', py: 1.8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuizIcon sx={{ color: '#93C5FD' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'white' }}>
              ผลคะแนนการสอบประมวลความรู้
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setExamResultDialogOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {(() => {
            const examDetails = taskData?.exam_details || {};
            const score = examDetails.score !== undefined && examDetails.score !== null 
              ? examDetails.score 
              : (taskData?.exam_score ?? 0);
            const totalPoints = examDetails.total_points || taskData?.exam_attempt_points || 100;
            const percentage = examDetails.percentage !== undefined && examDetails.percentage !== null
              ? Number(examDetails.percentage)
              : (totalPoints > 0 ? (Number(score) / Number(totalPoints)) * 100 : 0);
            const passingPercentage = examDetails.passing_percentage || 60;
            const isPassed = examDetails.is_passed !== undefined ? Boolean(examDetails.is_passed) : percentage >= passingPercentage;
            const submittedAt = examDetails.submitted_at || taskData?.exam_submitted_at;
            const examTitle = examDetails.exam_title || taskData?.exam_title || 'การสอบประมวลผลความรู้';

            return (
              <Box>
                {/* Result Hero Banner */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    borderRadius: 3,
                    mb: 3,
                    bgcolor: isPassed ? '#F0FDF4' : '#FEF2F2',
                    borderColor: isPassed ? '#86EFAC' : '#FCA5A5',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isPassed ? '#166534' : '#991B1B', mb: 0.5 }}>
                    {isPassed ? 'ผ่านเกณฑ์การสอบ (PASSED)' : 'ไม่ผ่านเกณฑ์การสอบ (FAILED)'}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: isPassed ? '#15803D' : '#DC2626', my: 1 }}>
                    {Number(score).toFixed(2)} <Typography component="span" variant="h5" sx={{ fontWeight: 600, color: 'text.secondary' }}>/ {totalPoints}</Typography>
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={`คิดเป็น ${percentage.toFixed(1)}%`}
                      color={isPassed ? 'success' : 'error'}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      (เกณฑ์ผ่านขั้นต่ำ: {passingPercentage}%)
                    </Typography>
                  </Box>
                </Paper>

                {/* Details Breakdown */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#F8FAFC' }}>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        ชุดข้อสอบ:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                        {examTitle}
                      </Typography>
                    </Grid>
                    {taskData?.course_code && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          รายวิชา:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {taskData.course_code} {taskData.course_name}
                        </Typography>
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        ส่งข้อสอบเมื่อ:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {submittedAt ? new Date(submittedAt).toLocaleString('th-TH') : '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                <Alert severity="info" sx={{ mt: 2.5, borderRadius: 2 }}>
                  ผลคะแนนนี้ถูกนำไปคำนวณร่วมในสัดส่วนคะแนนภาระงานของรายวิชาตามโครงสร้างหลักสูตรของคณะครุศาสตร์
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between', bgcolor: '#F8FAFC' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<QuizIcon fontSize="small" />}
            onClick={() => {
              setExamResultDialogOpen(false);
              navigate('/dashboard/exams');
            }}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            ไปยังหน้าระบบการสอบ (Exam Portal)
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setExamResultDialogOpen(false)}
            sx={{ borderRadius: 2, px: 3, fontWeight: 700 }}
          >
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentWorkloadSubmission;
